import { create } from 'zustand';
import {
    OnboardingBusinessNameStepDto,
    OnboardingFilesStepDto,
    OnboardingFundraisingStageStepDto,
    OnboardingWithFilesDto,
} from '@/lib/api/schemas/onboarding.schema';
import { ONBOARDING_FILE_TYPE } from '@/lib/api/types/common.enum';

type OnboardingState = {
    currentStep: number;
    unlockedStep: number;
    lastStep: number;
    setCurrentStep: (step: number) => void;
    onboardingCompleted: boolean;
    setOnboardingCompleted: (value: boolean) => void;
    onboardingInProgress: boolean;
    filesStep: OnboardingFilesStepDto;
    setFilesStep: (values: OnboardingFilesStepDto) => void;
    businessNameStep: OnboardingBusinessNameStepDto;
    setBusinessNameStep: (values: OnboardingBusinessNameStepDto) => void;
    fundraisingStageStep: Partial<OnboardingFundraisingStageStepDto>;
    setFundraisingStageStep: (
        values: OnboardingFundraisingStageStepDto,
    ) => void;
    setInitialOnboardingState: (data: OnboardingWithFilesDto) => void;
    clearOnboardingState: () => void;
};

export const useOnboardingStore = create<OnboardingState>((set, get) => ({
    currentStep: 0,
    unlockedStep: 0,
    lastStep: 3,
    onboardingCompleted: false,
    setOnboardingCompleted: (onboardingCompleted) =>
        set({ onboardingCompleted }),
    onboardingInProgress: get()?.currentStep !== null,
    setCurrentStep: (currentStep) =>
        set(({ unlockedStep }) => ({
            currentStep,
            unlockedStep: Math.max(unlockedStep, currentStep),
        })),
    filesStep: {
        businessPlan: undefined,
        executiveSummary: undefined,
        financialModels: undefined,
        offeringDocs: undefined,
        other: undefined,
        pitchDeck: undefined,
    },
    setFilesStep: (filesStep) => set({ filesStep }),
    businessNameStep: {
        businessName: '',
    },
    setBusinessNameStep: (businessNameStep) => set({ businessNameStep }),
    fundraisingStageStep: {
        fundraisingStage: undefined,
    },
    setFundraisingStageStep: (fundraisingStageStep) =>
        set({ fundraisingStageStep }),
    setInitialOnboardingState: (data: OnboardingWithFilesDto) => {
        const { businessName, fundraisingStage, activeStepIndex, files } = data;

        set({
            currentStep: activeStepIndex,
            unlockedStep: activeStepIndex,
        });

        const filesStep: OnboardingFilesStepDto = {};

        files.forEach((file) => {
            if (file.type) {
                filesStep[file.type as ONBOARDING_FILE_TYPE] = {
                    name: file.name,
                    objectId: file.objectId,
                };
            }
        });

        set({
            filesStep,
        });

        if (businessName) {
            set({
                businessNameStep: { businessName },
            });
        }

        if (fundraisingStage) {
            set({
                fundraisingStageStep: { fundraisingStage },
                onboardingCompleted: true,
            });
        }
    },
    clearOnboardingState: () => {
        set({
            currentStep: 0,
            unlockedStep: 0,
            lastStep: 3,
            onboardingCompleted: false,
            filesStep: {
                businessPlan: undefined,
                executiveSummary: undefined,
                financialModels: undefined,
                offeringDocs: undefined,
                other: undefined,
                pitchDeck: undefined,
            },
            businessNameStep: {
                businessName: '',
            },
            fundraisingStageStep: {
                fundraisingStage: undefined,
            },
        });
    },
}));
